import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, Subject, Subscription } from 'rxjs';
import {
  startWith,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs/operators';

// displayProperty is required when options are list of objects
type AutocompleteInput =
  | { options: Record<string, any>[]; displayProperty: string }
  | { options: string[]; displayProperty?: string };

type Option = Record<string, any> | string;

@Component({
    selector: 'zint-autocomplete',
    templateUrl: './zint-autocomplete.component.html',
    styleUrl: './zint-autocomplete.component.css',
    standalone: false
})
export class ZintAutocompleteComponent {
  @Input() options: AutocompleteInput['options'] = [];
  @Input() displayProperty?: AutocompleteInput['displayProperty']; //NB: Key for object options
  @Input() label: string = 'Select';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() preSelectedValue = null; // Optional initial selected item
  @Input() showIcon = false;
  @Input() subscriptString: string; // static subscript for all options
  @Input() subscriptKey: string; // obj key for key-value data
  @Input() iconClass: string = 'far fa-chevron-down fa-xs'; // font-awesome class

  @Output() optionSelected = new EventEmitter<Option>();
  @Output() inputCleared = new EventEmitter<void>();

  autocompleteControl = new FormControl<Option | null>(null);
  optionsSubject = new Subject<Option[]>(); // Subj to listen to options
  filteredOptions$!: Observable<Option[]>;
  valueChangesSubscription: Subscription;

  ngOnInit(): void {
    if (this.preSelectedValue) {
      this.autocompleteControl.setValue(this.preSelectedValue);
    }

    if (this.disabled) {
      this.autocompleteControl.disable();
    } else {
      this.autocompleteControl.enable();
    }

    this.filteredOptions$ = this.autocompleteControl.valueChanges.pipe(
      startWith(this.preSelectedValue || ''),
      debounceTime(100), // Debounce typing
      distinctUntilChanged((prev, curr) => {
        const prevDisplay = this.getOptionDisplay(prev);
        const currDisplay = this.getOptionDisplay(curr);
        return prevDisplay === currDisplay;
      }),

      switchMap(value => {
        return this.optionsSubject.pipe(
          startWith(this.options),
          map(latestOptions => {
            const filterValue =
              typeof value === 'string'
                ? value.toLowerCase()
                : this.getOptionDisplay(value).toLowerCase();

            return filterValue
              ? this.filterOption(filterValue, latestOptions)
              : [...latestOptions];
          })
        );
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      const { firstChange, currentValue } = changes['disabled'];

      if (!firstChange) {
        currentValue
          ? this.autocompleteControl.disable()
          : this.autocompleteControl.enable();
      }
    }
    if (changes['options']) {
      const { firstChange, currentValue } = changes['options'];
      if (!firstChange) {
        this.optionsSubject.next(currentValue);
        this.reset();
      }
    }
    if (changes['preSelectedValue']) {
      const { firstChange, currentValue } = changes['preSelectedValue'];
      if (!firstChange) {
        this.autocompleteControl.setValue(currentValue);
      }
    }
  }

  // Filtering logic for both string and object
  filterOption(filterValue: string, optionsToFilter: Option[]): Option[] {
    return optionsToFilter.filter(option =>
      this.getOptionDisplay(option).toLowerCase().includes(filterValue)
    );
  }

  // Method to display the selected item in the input field (handles both string and object)
  displayFn = (option: Option | null): string => {
    return this.getOptionDisplay(option);
  };

  // get the display string from either string or object
  getOptionDisplay(option: Option | null): string {
    //empty for non-zero falsy
    if (option === null || option === undefined) return '';

    // if string return as is
    if (typeof option === 'string') return option;

    // If it's an object and displayProperty is provided
    if (this.displayProperty && typeof option === 'object') {
      return String(option[this.displayProperty]);
    }
    // Fallback for objects without displayProperty
    return String(option);
  }

  emitSelectedOption(event: MatAutocompleteSelectedEvent): void {
    const selectedValue = event.option.value;
    this.optionSelected.emit(selectedValue);
  }

  onBlur(): void {
    const currentValue = this.autocompleteControl.value;
    if (currentValue === null || currentValue === undefined || currentValue === '') {
      this.inputCleared.emit();
    }
  }

  ngOnDestroy(): void {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
  }

  reset(): void {
    this.autocompleteControl.reset();
  }
}
